<script setup lang="ts">
import { useSessionStorage } from "@vueuse/core";
import { type JwtPayload, jwtDecode } from "jwt-decode";
import { computed } from "vue";

/*
  Component for receiving and processing data injected into the Vue app directly from the server;
  contents of `props` are supplied by blade template
 */
import { useStore } from "vuex";
import { resolveOktaAccessToken } from "@/util/auth";

const store = useStore();

const cloneJson = <T>(value: T): T => {
  // Bootstrap payloads should be JSON-serializable; cloning avoids leaking Vue proxies into Vuex state
  // (which can trigger strict-mode "mutate outside mutation" errors).
  return value === null ? value : JSON.parse(JSON.stringify(value));
};

const props = defineProps({
  currentUser: {
    type: [String, Object],
    default: null,
  },
  learnerTypes: {
    type: Array,
    default: () => [],
  },
  oktaToken: {
    type: String,
    default: null,
  },
  brandingDetails: {
    type: Object,
    default: () => ({}),
  },
  domainEntityId: {
    type: String,
    default: null,
  },
  oktaAnonymousToken: {
    type: String,
    default: undefined,
  },
});

// This query parameter will allow us to bypass laravel authentication
// on the fronted when we do playwright test
const testMode = computed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("test-mode") === "true";
});

const bootstrapFromServer = () => {
  if (!testMode.value) {
    const hasOktaToken = typeof props.oktaToken === "string" && props.oktaToken.trim() !== "";
    try {
      // `currentUser` is injected by Blade; on unauth pages it may be null/empty.
      // Historically this prop has sometimes arrived as a JSON string, so we support both.
      let currentUser: unknown = null;
      if (typeof props.currentUser === "string" && props.currentUser.trim() !== "") {
        currentUser = JSON.parse(props.currentUser);
      } else if (props.currentUser && typeof props.currentUser === "object") {
        currentUser = cloneJson(props.currentUser);
      }

      if (currentUser) {
        store.commit("setCurrentUser", cloneJson(currentUser) as Record<string, unknown>);
      } else {
        if (hasOktaToken) {
          // If we have an Okta token but the Blade bootstrap didn't include currentUser,
          // fetch it so permission-based UI (e.g. sidebar items) is accurate.
          // This avoids incorrectly clearing persisted auth state for authenticated loads.
          const inMemoryAccessToken = resolveOktaAccessToken(store.state.currentUser) !== undefined;
          if (!store.state.currentUser || !inMemoryAccessToken) {
            store.dispatch("refreshUserDataFromServer");
          }
        } else {
          // Important for pages like `/sign-in`: vuex-persistedstate may rehydrate a stale user from
          // sessionStorage. If the server did not provide a user, treat this page load as unauthenticated.
          store.commit("setCurrentUser", null);
        }
      }
    } catch (e) {
      console.warn("Failed to parse currentUser bootstrap payload", e);
    }

    if (hasOktaToken) {
      // Decode the token and save only entity_id
      const decodedToken = jwtDecode<JwtPayload & { entity_ids: string }>(props.oktaToken);
      const entityId = decodedToken.entity_ids;
      if (Array.isArray(entityId) && entityId.length > 0) {
        const entityObjects = entityId.map((id) => ({ id }));
        store.commit("setEntityIDs", entityObjects);
      } else {
        store.commit("setEntityIDs", []);
        console.warn("entity_id not found in token");
      }
    }
  }

  // Returns a reactive ref synced with sessionStorage
  const domainEntityIdOverride = useSessionStorage("deio", ""); // key, default value
  // `deio` is a dev/QA preview override; production always uses the server-provided domain entity.
  const allowEntityOverride = import.meta.env.VITE_APP_ENV !== "production";
  if (!allowEntityOverride || !domainEntityIdOverride.value) {
    store.commit("setDomainEntityId", props.domainEntityId);
  } else {
    store.commit("setDomainEntityId", domainEntityIdOverride.value);
  }

  if (props.oktaAnonymousToken) {
    store.commit("setAnonToken", props.oktaAnonymousToken);
  }

  store.dispatch("learnerTypes/initialize", props.learnerTypes);

  store.dispatch("updateBrandingStyles", cloneJson(props.brandingDetails));

  store.dispatch("setSelectedEntityViewOnAppLoad");
};

// Run bootstrap immediately so child components don't race store initialization.
bootstrapFromServer();
</script>

<template>
  <slot />
</template>
